import React, { useState } from 'react';
import { Box, Text, Button, Checkbox, Link } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FaCcVisa, FaUniversity, FaDonate } from 'react-icons/fa';

// Mirror the Quotation type structure from SAL125
export type AskSalahQuotation = {
  id?: string;
  quotationId?: number;
  planType: 'trial' | 'basic' | 'premium';
  billingCycle?: 'monthly' | 'yearly';
  price: number;
  sessionsPerMonth: number;
  features: string[];
  selected?: string; // Selected payment method
  proveUrl?: string; // Proof of payment URL for manual payments
  isPopular?: boolean;
};

interface AskSalahSubscriptionCardProps {
  quotation: AskSalahQuotation;
  selectMethod: (id: string, method: string) => void;
  handleOpening: (quotation: AskSalahQuotation) => void;
  subscribeAskSalah: (subData: {}) => void;
  processing: boolean;
  isCurrentPlan?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  updateProveUrl?: (id: string, proveUrl: string) => void; // NEW: For manual payment proof updates
}

const paymentMethods = [
  { name: 'card', Icon: FaCcVisa, color: '#6c5fe3' },
  { name: 'purchase', Icon: FaDonate, color: '#0abf25' },
  { name: 'bank', Icon: FaUniversity, color: '#f5b464' },
];

const AskSalahSubscriptionCard: React.FC<AskSalahSubscriptionCardProps> = ({
  quotation,
  selectMethod,
  handleOpening,
  subscribeAskSalah,
  processing,
  isCurrentPlan = false,
  disabled = false,
  disabledReason,
  updateProveUrl,
}) => {
  const [agreeOnTermsAndConditions, setAgreeOnTermsAndConditions] = useState<boolean>(false);
  const intl = useIntl();
  const { locale } = intl;

  const getPlanTitle = () => {
    switch (quotation.planType) {
      case 'trial':
        return intl.formatMessage({ id: 'askSalah.trial.name', defaultMessage: 'Free Trial' });
      case 'basic':
        return intl.formatMessage({ id: 'askSalah.basic.name', defaultMessage: 'Basic Plan' });
      case 'premium':
        return intl.formatMessage({ id: 'askSalah.premium.name', defaultMessage: 'Premium Plan' });
      default:
        return quotation.planType;
    }
  };

  const getButtonText = () => {
    if (isCurrentPlan) {
      return intl.formatMessage({ id: 'askSalah.button.currentPlan', defaultMessage: 'Current Plan' });
    }
    if (quotation.planType === 'trial') {
      return intl.formatMessage({ id: 'askSalah.button.startTrial', defaultMessage: 'Start Free Trial' });
    }
    return intl.formatMessage({ id: 'askSalah.button.subscribeNow', defaultMessage: 'Subscribe' });
  };


  const isFreeTrial = quotation.planType === 'trial';
  const sessionsLabelId = isFreeTrial
    ? 'askSalah.trial.sessionsPerPeriod'
    : 'askSalah.sessionsPerMonth';
  const sessionsLabelDefault = isFreeTrial
    ? '{count} sessions / 7 days'
    : '{count} sessions/month';

  return (
    <Box
      display="flex"
      flexDir="column"
      alignItems="center"
      padding="30px 0"
      borderRadius="15px"
      backgroundColor="white"
      boxShadow="0 2px 10px 2px silver"
      margin={{
        base: "30px 0 !important",
        lg: "30px !important",
      }}
      width={{ base: "300px", md: "350px" }}
      position="relative"
      overflow="hidden"
      opacity={disabled ? 0.6 : 1}
    >
      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <Box
          position="absolute"
          top="10px"
          left="10px"
          backgroundColor="green.500"
          color="white"
          fontSize="xs"
          fontWeight="bold"
          px={2}
          py={1}
          borderRadius="md"
          textTransform="uppercase"
        >
          <FormattedMessage id="askSalah.button.currentPlan" defaultMessage="Current" />
        </Box>
      )}

      {/* Plan Title */}
      <Box padding="0 30px">
        <Text fontSize="36px" fontWeight="bold" color="brand.900">
          {getPlanTitle()}
        </Text>
      </Box>

      {/* Price Section - Matching SAL125 Design */}
      <Box
        backgroundColor="brand.900"
        padding="30px 0"
        width="100%"
        marginTop="30px"
        maxHeight="100px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Text
          textAlign="center"
          color="white"
          fontSize="28px"
          fontWeight="bold"
        >
          {quotation.price === 0 ? (
            <FormattedMessage id="askSalah.free" defaultMessage="Free" />
          ) : (
            <>
              {quotation.price.toFixed(2)}
              <sup>$</sup>
              {quotation.billingCycle && (
                <Text as="span" fontSize="18px" ml={1}>
                  <FormattedMessage 
                    id={quotation.billingCycle === 'monthly' ? 'askSalah.perMonth' : 'askSalah.perYear'} 
                    defaultMessage={quotation.billingCycle === 'monthly' ? '/month' : '/year'} 
                  />
                </Text>
              )}
            </>
          )}
        </Text>
      </Box>

      {/* Payment Methods Section - Same as SAL125 */}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        marginTop="30px"
      >
        {!isFreeTrial ? (
          paymentMethods.map(({ name, Icon, color }) => (
            <Box
              title={name}
              key={name}
              margin="15px"
              width="80px"
              paddingTop="10px"
              display="flex"
              flexDir="column"
              alignItems="center"
              borderRadius="10px"
              background={name === quotation.selected ? "#b2caf7" : "white"}
              onClick={() => {
                if (!disabled) {
                  selectMethod(quotation.id!, name);
                }
              }}
              cursor={disabled ? "not-allowed" : "pointer"}
              transition="150ms ease-out"
              _hover={{ background: disabled ? "white" : "#b2caf7" }}
            >
              <Icon size={46} height="70px" width="70px" color={color} />
              <Text my={3} fontWeight="600">
                <FormattedMessage id={name || ""} />
              </Text>
            </Box>
          ))
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="134px"
            flexDirection="column"
          >
            <Text
              color="brand.900"
              textStyle="smallBold"
              fontWeight="800"
              fontSize="20px"
              padding={locale === "ar" ? "0 5px" : "0"}
              mb={2}
            >
              <FormattedMessage
                id={sessionsLabelId}
                defaultMessage={sessionsLabelDefault}
                values={{ count: quotation.sessionsPerMonth }}
              />
            </Text>
            <Text fontSize="14px" color="gray.600" textAlign="center">
              <FormattedMessage id="askSalah.feature.textChat" defaultMessage="Chat-based only" />
            </Text>
          </Box>
        )}
      </Box>


      {/* Features Section */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop="30px"
        padding="0 30px"
        width="100%"
      >
        <Text fontSize="20px" fontWeight="600" color="gray.700" mb={4}>
          <FormattedMessage
            id={sessionsLabelId}
            defaultMessage={sessionsLabelDefault}
            values={{ count: quotation.sessionsPerMonth }}
          />
        </Text>

        {/* Features List */}
        <Box width="100%" textAlign="left">
          {quotation.features.map((feature, index) => (
            <Text
              key={index}
              fontSize="14px"
              color="gray.600"
              mb={2}
              pl={4}
              _before={{
                content: '"✓"',
                color: 'green.500',
                fontWeight: 'bold',
                marginRight: '8px',
              }}
            >
              {intl.formatMessage({ id: feature, defaultMessage: feature })}
            </Text>
          ))}
        </Box>
      </Box>

      {/* Subscribe Button */}
      <Box marginTop="30px">
        {processing && (
          <Box margin="30px 0">
            <Text color="grey">
              <FormattedMessage id="wait" />
            </Text>
          </Box>
        )}
        <Button
          backgroundColor="brand.900"
          color="white"
          height="50px"
          _hover={{ filter: "brightness(120%)" }}
          onClick={() => {
            // Trial flow - directly call the free trial API
            if (isFreeTrial) {
              handleOpening(quotation);
              return;
            }

            // For paid plans - use handleOpening to trigger modal for bank payments
            handleOpening(quotation);
          }}
          disabled={
            disabled ||
            isCurrentPlan ||
            (!isFreeTrial && !quotation.selected) ||
            processing ||
            !agreeOnTermsAndConditions
          }
          px={8}
        >
          {getButtonText()}
        </Button>
      </Box>


      {/* Terms and Conditions Checkbox - Same as SAL125 */}
      <Checkbox
        type="checkbox"
        name="complete"
        isChecked={agreeOnTermsAndConditions}
        onChange={() => setAgreeOnTermsAndConditions((prev) => !prev)}
        marginTop="20px"
        isDisabled={disabled}
      >
        <FormattedMessage id="agreeTerms" />{" "}
        <Link
          href="https://asksalah.smartsway.com/terms-and-conditions"
          isExternal
          color="brand.900"
          textStyle="smallBold"
        >
          <FormattedMessage id="t&c" />
        </Link>
      </Checkbox>

    </Box>
  );
};

export default AskSalahSubscriptionCard;
